/**
 * useJsonLd — safely inject a schema.org JSON-LD `<script>` tag into
 * `document.head` and remove it on unmount.
 *
 * Why not `<Helmet><script type="application/ld+json">…</script></Helmet>`?
 * react-helmet-async does not reliably lift inline `<script>` JSX children
 * into `<head>` in React 18 concurrent-mode — they end up rendered in the
 * body, invisible to Google/Gemini/Perplexity's schema.org crawlers.
 * useEffect + direct DOM manipulation on `document.head` works everywhere.
 *
 * Usage:
 *   useJsonLd('breadcrumb', breadcrumbJson);
 *   useJsonLd('article', articleJson);
 *   useJsonLd('faq', faqJson);      // pass null/undefined to skip
 *
 * The `id` argument namespaces the script so the same page can inject
 * multiple schemas without collision, and re-renders overwrite cleanly.
 */
import { useEffect } from 'react';

export default function useJsonLd(id, data) {
  useEffect(() => {
    if (!data) return undefined;
    const elId = `ld-json-${id}`;
    let script = document.getElementById(elId);
    if (!script) {
      script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = elId;
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(data);
    return () => {
      const existing = document.getElementById(elId);
      if (existing) existing.remove();
    };
  }, [id, data]);
}
